import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import {HEADER, sendMobileAnalyticsEvent} from '@optimaxdev/analytics/mobile';

import RadioButton from 'components/__legacy/radioButton/radioButton';
import {Icon} from 'components/baseIcon/baseIcon';
import * as PersonIcon from 'components/__helpers/icon/svg/common/person.svg';
import {LOGIN, REGISTER, FORGOT, AVALIABLE_MENU_STATE} from 'features/menu/constants';
import {setAuthOption as setAuthOptionAction, getCustomer} from 'reducers/oAuth/oAuth';
import {SocialButtonsConnected} from 'features/menu/components/socialButtons/socialButtons';
import {OPEN_MENU_ITEMS_TO_OPEN} from 'reducers/menu/visibilityMenuType';
import {ActiveTab} from 'features/menu/helpers/authorization/activeTab/activeTab';
import {RADIO_BUTTON_LIST} from 'features/menu/helpers/authorization/constants';
import {ApplicationStoreType} from 'constants/flow/flowTypes';

import s from './authorization.scss';

export type PropsType = {
    formKey: string;
    visibilityMenu: {
        openMenuItem: string;
        visible: boolean;
    };
    isOpened: boolean;
    setAuthOption: (authOption: LOGIN | FORGOT | REGISTER) => void;
    usefulMessage: string;
    isMenuOpened: boolean;
};

/**
 *  Authorization component
 *  @param {PropsType} - PropsType
 *  @returns {React.ReactElement} - rendered component
 */
export const Authorization: FC<PropsType> = ({
    formKey,
    visibilityMenu,
    isOpened,
    setAuthOption,
    usefulMessage,
    isMenuOpened,
}) => {
    const [isVisible, setVisibility] = useState(isOpened);
    const [activeTab, setActiveTab] = useState(LOGIN);
    const [wasOpened, setOpened] = useState(false);

    useEffect(() => {
        if (isMenuOpened && isVisible) setOpened(true);
    }, [isMenuOpened, isVisible]);

    useEffect(() => {
        if (
            visibilityMenu.visible &&
            visibilityMenu.openMenuItem !== OPEN_MENU_ITEMS_TO_OPEN.DEFAULT
        ) {
            setVisibility(true);
            setActiveTab(AVALIABLE_MENU_STATE[visibilityMenu.openMenuItem] || LOGIN);
        }
    }, [visibilityMenu.visible, visibilityMenu.openMenuItem]);

    /**
     * Change Authorization section visibility if not opened by default (checkout only)
     */
    const toggleBlockVisibility = () => {
        if (!isOpened) {
            sendMobileAnalyticsEvent(HEADER.BUTTON, {
                buttonName: 'Login Or SignUp',
                label: '',
            });
            setVisibility(!isVisible);
        }
    };

    /**
     * Change current tab to received
     * @param {string} name - tab name
     */
    const changeTab = (name: string) => {
        setActiveTab(name);
        setAuthOption(name);
    };

    return (
        <div className={s.wrap}>
            <button
                className={classNames(s.wrapName, {[s.opened]: isVisible}, s.attention)}
                data-test-name="login"
                onClick={toggleBlockVisibility}
                type="button"
            >
                <span className={s.name}>Sign in / Sign up</span>
                <div className={s.icon}>
                    <Icon icon={PersonIcon} role="presentation" />
                </div>
            </button>
            <p className={s.message}>{usefulMessage}</p>

            <AnimateHeight height={isVisible ? 'auto' : 0}>
                <div className={s.form} data-test-name="authWrapper">
                    {activeTab !== FORGOT && (
                        <>
                            <ul className={s.radioBlock}>
                                {RADIO_BUTTON_LIST.map(({value, dataName, dataValue, text}) => (
                                    <li
                                        key={value}
                                        className={s.radio}
                                        data-test-name={dataName}
                                        data-test-value={dataValue}
                                    >
                                        <RadioButton
                                            inline
                                            name="account"
                                            value=""
                                            checked={activeTab === value}
                                            customStyle={s.radioBox}
                                            handleClick={() => changeTab(value)}
                                        >
                                            {text}
                                        </RadioButton>
                                    </li>
                                ))}
                            </ul>
                            <p className={s.titleSignUp}>
                                {activeTab === LOGIN ? 'Log in with:' : 'Sign up with:'}
                            </p>
                            {wasOpened ? (
                                <SocialButtonsConnected isLogin={activeTab === LOGIN} />
                            ) : (
                                <ul className={s.socialButtonsSkeleton}>
                                    <li className={s.socialButtonSkeleton} />
                                    <li className={s.socialButtonSkeleton} />
                                </ul>
                            )}
                            <p className={s.or}>
                                {activeTab === LOGIN
                                    ? 'Or Log in with email:'
                                    : 'Or sign up with your email:'}
                            </p>
                        </>
                    )}
                    <ActiveTab
                        activeTab={activeTab}
                        formKey={formKey}
                        visibleTabHandler={changeTab}
                    />
                </div>
            </AnimateHeight>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (store: ApplicationStoreType) => ({
    formKey: store.customer.form_key,
    visibilityMenu: store.visibilityMenu,
    usefulMessage: getCustomer(store).notificationMessage,
});

export const AuthorizationConnected = connect(mapStateToProps, {
    setAuthOption: setAuthOptionAction,
})(Authorization);
